import { StyleSheet, Platform } from 'react-native';
import type { Theme } from '../../../src/theme/getTheme';
import type { ThemeMode } from '../../../src/theme/ThemeContext';

export type MatchCardLayoutMode = 'default' | 'tutorial';

export interface MatchCardFinishedStyleOptions {
  /** Wider card for welcome rules preview on narrow screens */
  layout?: MatchCardLayoutMode;
}

export const getMatchCardFinishedStyles = (
  theme: Theme,
  themeMode: ThemeMode = 'light',
  options?: MatchCardFinishedStyleOptions,
) => {
  const isDark = themeMode === 'dark';
  const tutorialWide = options?.layout === 'tutorial';
  return StyleSheet.create({
    finishedCard: {
      backgroundColor: theme.background.card,
      borderRadius: 24,
      width: tutorialWide ? '100%' : '94%',
      alignSelf: tutorialWide ? ('stretch' as const) : ('center' as const),
      marginBottom: 14,
      borderWidth: 0,
      overflow: 'hidden',
      shadowColor: theme.shadow,
      shadowOffset: { width: 0, height: 8 },
      shadowOpacity: 0.1,
      shadowRadius: 14,
      elevation: 8,
      ...(Platform.OS === 'web' && {
        maxWidth: tutorialWide ? ('100%' as never) : 520,
      }),
    },
    finishedCardHit: {
      borderWidth: 1,
      borderColor: theme.success + '4D',
    },
    finishedCardResult: {
      borderWidth: 1,
      borderColor: theme.info + '4D',
    },
    finishedCardMiss: {
      borderWidth: 1,
      borderColor: theme.error + '4D',
    },
    /** Boost glow on finished cards — matches outcome (Exact / Result / Miss). */
    finishedCardBoostedHit: {
      shadowColor: theme.success,
      shadowOpacity: 0.55,
      shadowRadius: 14,
      shadowOffset: { width: 0, height: 0 },
      elevation: 8,
      ...(Platform.OS === 'web' && {
        boxShadow: `0 0 18px 2px ${theme.success}8C` as never,
      }),
    },
    finishedCardBoostedResult: {
      shadowColor: theme.info,
      shadowOpacity: 0.55,
      shadowRadius: 14,
      shadowOffset: { width: 0, height: 0 },
      elevation: 8,
      ...(Platform.OS === 'web' && {
        boxShadow: `0 0 18px 2px ${theme.info}8C` as never,
      }),
    },
    finishedCardBoostedMiss: {
      shadowColor: theme.error,
      shadowOpacity: 0.55,
      shadowRadius: 14,
      shadowOffset: { width: 0, height: 0 },
      elevation: 8,
      ...(Platform.OS === 'web' && {
        boxShadow: `0 0 18px 2px ${theme.error}8C` as never,
      }),
    },
    finishedBoostMiniBadgeHit: {
      borderColor: theme.success + '66',
      backgroundColor: theme.success + '1A',
    },
    finishedBoostMiniBadgeResult: {
      borderColor: theme.info + '66',
      backgroundColor: theme.info + '1A',
    },
    finishedBoostMiniBadgeMiss: {
      borderColor: theme.error + '66',
      backgroundColor: theme.error + '1A',
    },
    finishedBoostMiniBadgeTextHit: {
      color: theme.success,
    },
    finishedBoostMiniBadgeTextResult: {
      color: theme.info,
    },
    finishedBoostMiniBadgeTextMiss: {
      color: theme.error,
    },
    accentBar: {
      height: 4,
      width: '100%',
    },
    accentBarHit: {
      backgroundColor: theme.success,
    },
    accentBarResult: {
      backgroundColor: theme.info,
    },
    accentBarMiss: {
      backgroundColor: theme.error,
    },
    finishedContent: {
      zIndex: 1,
      paddingHorizontal: 14,
      paddingTop: 12,
      paddingBottom: 0,
      gap: 12,
    },
    finishedTopRow: {
      flexDirection: 'row' as const,
      alignItems: 'flex-start' as const,
      justifyContent: 'space-between' as const,
      flexWrap: 'wrap' as const,
      gap: 8,
    },
    finishedStageBadge: {
      fontSize: 11,
      fontWeight: '700' as const,
      color: theme.white,
      backgroundColor: theme.white + '33',
      borderRadius: 16,
      paddingHorizontal: 10,
      paddingVertical: 5,
      flexShrink: 1,
    },
    fullTimeText: {
      fontSize: 10,
      fontWeight: '700' as const,
      color: theme.white,
      textTransform: 'uppercase' as const,
      letterSpacing: 0.8,
      opacity: 0.95,
    },
    finishedMatchRow: {
      flexDirection: 'row' as const,
      // Top-align so the score sits level with the flags (which are at the top of each team
      // block), not centered against the taller flag+name+rank column below them.
      alignItems: 'flex-start' as const,
      justifyContent: 'space-between' as const,
    },
    finishedTeamBlock: {
      flex: 1,
      minWidth: 0,
      alignItems: 'center' as const,
      gap: 6,
    },
    finishedTeamName: {
      fontSize: 12,
      fontWeight: '700' as const,
      color: isDark ? theme.black : theme.gray[800],
      textAlign: 'center' as const,
      alignSelf: 'stretch' as const,
    },
    finishedScoreBlock: {
      alignItems: 'center' as const,
      paddingHorizontal: 8,
    },
    finishedScoreRow: {
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      gap: 10,
    },
    finishedScoreNum: {
      fontSize: 32,
      lineHeight: 36,
      fontWeight: '900' as const,
      color: isDark ? theme.black : theme.gray[900],
      minWidth: 24,
      textAlign: 'center' as const,
    },
    finishedScoreDash: {
      fontSize: 20,
      fontWeight: '700' as const,
      color: theme.gray[500],
    },
    // --- Score for knockout matches decided beyond 90 minutes ---
    // Final score stays the big primary number; the 90-minute result (what the pick was
    // scored on) reads as a smaller sub-line below it, with any shootout in between.
    finishedScoreStacked: {
      alignItems: 'center' as const,
      gap: 2,
    },
    finishedScorePens: {
      fontSize: 10,
      fontWeight: '700' as const,
      color: theme.gray[500],
      textTransform: 'uppercase' as const,
      letterSpacing: 0.4,
    },
    finishedRegulationLine: {
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      gap: 5,
      marginTop: 1,
    },
    finishedRegulationLabel: {
      fontSize: 9,
      fontWeight: '700' as const,
      textTransform: 'uppercase' as const,
      letterSpacing: 0.5,
      color: theme.gray[500],
    },
    finishedRegulationScore: {
      fontSize: 13,
      fontWeight: '800' as const,
      color: theme.gray[600],
    },
    finishedFooter: {
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      justifyContent: 'space-between' as const,
      paddingTop: 10,
    },
    finishedFooterLeft: {
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      gap: 10,
      paddingLeft: 8,
    },
    finishedPredictionText: {
      fontSize: 12,
      fontWeight: '500' as const,
      color: isDark ? theme.gray[500] : theme.gray[600],
    },
    finishedOutcomeLabel: {
      fontSize: 11,
      fontWeight: '700' as const,
    },
    // Subtle "(90′)" marker appended to the LIVE badge when it is judged on the 90-minute
    // result while the card shows a diverging running scoreline (knockout with ET goals).
    live90MinCue: {
      fontSize: 9,
      fontWeight: '600' as const,
      color: isDark ? theme.gray[500] : theme.gray[600],
    },
    outcomeLabelHit: {
      color: theme.success,
    },
    outcomeLabelResult: {
      color: theme.info,
    },
    outcomeLabelMiss: {
      color: theme.error,
    },
    finishedPointsPos: {
      fontSize: 14,
      fontWeight: '800' as const,
    },
    finishedPointsHit: {
      color: theme.success,
    },
    finishedPointsResult: {
      color: theme.info,
    },
    finishedPointsNeg: {
      fontSize: 14,
      fontWeight: '600' as const,
      color: theme.error,
    },
    // Neutral "Scoring…" placeholder shown while a finished pick is still being settled.
    finishedPointsPending: {
      fontSize: 12,
      fontWeight: '600' as const,
      fontStyle: 'italic' as const,
      color: theme.gray[500],
    },
    // Late joiner: this match kicked off before they joined, so they hold no pick (forfeited → 0).
    finishedMissedRow: {
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      paddingTop: 10,
      paddingLeft: 8,
    },
    finishedMissedText: {
      fontSize: 12,
      fontWeight: '600' as const,
      fontStyle: 'italic' as const,
      color: theme.gray[500],
    },
    liveBadge: {
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      gap: 5,
      borderRadius: 999,
      borderWidth: 0,
      backgroundColor: theme.error + '33',
      paddingHorizontal: 8,
      paddingVertical: 3,
    },
    liveDot: {
      width: 6,
      height: 6,
      borderRadius: 3,
      backgroundColor: theme.error,
    },
    liveText: {
      fontSize: 10,
      fontWeight: '800' as const,
      color: theme.white,
      textTransform: 'uppercase' as const,
      letterSpacing: 0.8,
    },
  });
};
